'use client';

import type { ICollectionPipelineStatusV3 } from '@repo/sdk';
import { fillMissingClipsApi, relinkClipsApi } from '@repo/sdk';
import type { JSX } from 'react';
import { useCallback } from 'react';
import { PipelineStepCell } from '../pipeline-step-cell';
import { PipelineStepDialog } from '../pipeline-step-dialog';
import { PipelineStepPlainCountBadge } from '../pipeline-step-plain-count-badge';
import { useStepAction } from '../use-step-action';
import { MissingClipsPanel } from './missing-clips-panel';

interface IMissingStepProps {
  item: ICollectionPipelineStatusV3;
}

export function MissingStep({ item }: IMissingStepProps): JSX.Element {
  const { clipsMissing } = item.steps;
  const collectionId = item.collectionId;
  const startFill = useCallback(() => fillMissingClipsApi(collectionId), [collectionId]);
  const startRelink = useCallback(() => relinkClipsApi(collectionId), [collectionId]);
  const fillAction = useStepAction({ start: startFill });
  const relinkAction = useStepAction({ start: startRelink });
  const activeAction = fillAction.status === 'idle' ? relinkAction : fillAction;
  const anyPending = fillAction.status === 'running' || relinkAction.status === 'running';

  return (
    <PipelineStepCell
      badge={<PipelineStepPlainCountBadge step={clipsMissing} />}
      renderDialog={(open, onOpenChange) => (
        <PipelineStepDialog
          open={open}
          onOpenChange={onOpenChange}
          title="Missing clips"
          description={`${clipsMissing.count} active challenge(s) with no clip. "Fill" creates 1 clip per missing. "Re-link all" adds clips for every title occurrence (heavier).`}
          primaryAction={{
            label: 'Fill missing clips',
            onClick: fillAction.run,
            pending: fillAction.status === 'running',
            disabledReason:
              clipsMissing.count === 0
                ? 'No missing clips'
                : anyPending && fillAction.status !== 'running'
                  ? 'Re-link in progress'
                  : undefined,
          }}
          secondaryAction={{
            label: 'Force re-link all',
            onClick: relinkAction.run,
            pending: relinkAction.status === 'running',
            disabledReason:
              anyPending && relinkAction.status !== 'running' ? 'Fill in progress' : undefined,
          }}
          body={
            <MissingClipsPanel
              collectionId={collectionId}
              count={clipsMissing.count}
              status={activeAction.status}
              log={activeAction.log}
            />
          }
        />
      )}
    />
  );
}
